/**
 * Wniosek o członka rodziny z portalu (Etap 37, ADR-0038).
 *
 * Obowiązek poinformowania płatnika ciąży na ubezpieczonym, a dokument ZUS ZCNA
 * składa płatnik w ciągu siedmiu dni od dnia, w którym się dowiedział. Dlatego
 * dniem poinformowania jest dzień ZŁOŻENIA tego wniosku — pracownik nie wpisuje
 * żadnej daty „poinformowania", bo to nie jest jego oświadczenie o przeszłości.
 *
 * O tym, czy dana osoba spełnia warunki członka rodziny, system nie orzeka:
 * formularz zbiera dane z ZCNA, a resztę rozstrzyga oświadczenie pracownika
 * i decyzja kadr.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  useToast,
} from '@k1p4/ui'
import { DISABILITY_DEGREES, KINSHIP_CODES } from '@k1p4/shared'
import { ApiError, api, type FamilyMemberItem } from '../../api/client'
import styles from '../app/ChangeRequestModal.module.css'

export interface FamilyRequestModalProps {
  open: boolean
  onClose: () => void
  /** Zgłoszeni członkowie rodziny — do wniosku o utratę uprawnień. */
  members: readonly FamilyMemberItem[]
  /** Członek wybrany z listy w portalu; pusty = pracownik wybiera w formularzu. */
  preselected?: string | null
  onSubmitted?: () => void
}

type Action = 'zgloszenie' | 'wyrejestrowanie'

interface Form {
  firstName: string
  lastName: string
  pesel: string
  documentKind: '' | '1' | '2'
  documentNumber: string
  birthDate: string
  kinshipCode: string
  disabilityCode: string
  sharedHousehold: boolean
  entitlementFrom: string
}

const EMPTY: Form = {
  firstName: '',
  lastName: '',
  pesel: '',
  documentKind: '',
  documentNumber: '',
  birthDate: '',
  kinshipCode: '',
  disabilityCode: '0',
  sharedHousehold: false,
  entitlementFrom: '',
}

export function FamilyRequestModal({
  open,
  onClose,
  members,
  preselected,
  onSubmitted,
}: FamilyRequestModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const ids = {
    action: useId(),
    firstName: useId(),
    lastName: useId(),
    pesel: useId(),
    documentKind: useId(),
    documentNumber: useId(),
    birthDate: useId(),
    kinship: useId(),
    disability: useId(),
    household: useId(),
    from: useId(),
    member: useId(),
    to: useId(),
  }

  const [action, setAction] = useState<Action>(
    preselected ? 'wyrejestrowanie' : 'zgloszenie',
  )
  const [form, setForm] = useState<Form>(EMPTY)
  const [memberId, setMemberId] = useState(preselected ?? '')
  const [entitlementTo, setEntitlementTo] = useState('')
  const [error, setError] = useState<string | null>(null)

  /* Wyrejestrować da się tylko kogoś, kto uprawnień jeszcze nie stracił. */
  const active = members.filter((member) => member.entitlementTo === null)

  const submit = useMutation({
    mutationFn: () =>
      api.submitRequest({
        type: 'czlonek_rodziny',
        family:
          action === 'zgloszenie'
            ? {
                action,
                member: {
                  firstName: form.firstName.trim(),
                  lastName: form.lastName.trim(),
                  ...(form.pesel.trim() ? { pesel: form.pesel.trim() } : {}),
                  ...(form.documentKind ? { documentKind: form.documentKind } : {}),
                  ...(form.documentNumber.trim()
                    ? { documentNumber: form.documentNumber.trim() }
                    : {}),
                  ...(form.birthDate ? { birthDate: form.birthDate } : {}),
                  kinshipCode: form.kinshipCode,
                  disabilityCode: form.disabilityCode,
                  sharedHousehold: form.sharedHousehold,
                  entitlementFrom: form.entitlementFrom,
                },
              }
            : { action, memberId, entitlementTo },
      }),
    onSuccess: () => {
      toast.show(t('family.requestSubmitted'), { tone: 'success' })
      setForm(EMPTY)
      setMemberId('')
      setEntitlementTo('')
      setError(null)
      onSubmitted?.()
      onClose()
    },
    onError: (caught) => {
      const code = caught instanceof ApiError ? caught.code : null
      setError(
        code === 'MEMBER_ID_REQUIRED'
          ? 'family.requestErrorIdRequired'
          : code === 'MEMBER_BIRTH_DATE_REQUIRED'
            ? 'family.requestErrorBirthDate'
            : code === 'FAMILY_MEMBER_NOT_FOUND'
              ? 'family.requestErrorMemberMissing'
              : code === 'NO_HEALTH_INSURANCE_TITLE'
                ? 'family.notInsured'
                : code === 'ENTITLEMENT_BEFORE_START'
                  ? 'family.requestErrorBeforeStart'
                  : 'family.requestErrorGeneric',
      )
    },
  })

  const ready =
    action === 'zgloszenie'
      ? form.firstName.trim() !== '' &&
        form.lastName.trim() !== '' &&
        form.kinshipCode !== '' &&
        form.entitlementFrom !== ''
      : memberId !== '' && entitlementTo !== ''

  return (
    <Modal open={open} onClose={onClose} title={t('family.requestTitle')}>
      <p className={styles.lead}>{t('family.requestLead')}</p>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.grid}>
        <FormField label={t('family.fields.action')} htmlFor={ids.action}>
          <Select
            id={ids.action}
            value={action}
            onChange={(event) => {
              setError(null)
              setAction(event.target.value as Action)
            }}
          >
            <option value="zgloszenie">{t('family.actions.zgloszenie')}</option>
            <option value="wyrejestrowanie" disabled={active.length === 0}>
              {t('family.actions.wyrejestrowanie')}
            </option>
          </Select>
        </FormField>

        {action === 'zgloszenie' ? (
          <>
            <FormField label={t('family.fields.firstName')} htmlFor={ids.firstName}>
              <Input
                id={ids.firstName}
                value={form.firstName}
                onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              />
            </FormField>
            <FormField label={t('family.fields.lastName')} htmlFor={ids.lastName}>
              <Input
                id={ids.lastName}
                value={form.lastName}
                onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('family.fields.kinship')}
              htmlFor={ids.kinship}
              hint={t('family.fields.kinshipHint')}
            >
              <Select
                id={ids.kinship}
                value={form.kinshipCode}
                onChange={(event) => setForm({ ...form, kinshipCode: event.target.value })}
              >
                <option value="">{t('family.fields.kinshipPlaceholder')}</option>
                {KINSHIP_CODES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {t(`family.kinship.${entry.code}`)}
                  </option>
                ))}
              </Select>
            </FormField>
            {/*
              Numer PESEL albo dokument tożsamości i data urodzenia — formularz
              ZCNA dopuszcza jedno z dwojga i tego samego pilnuje serwer.
            */}
            <FormField
              label={t('family.fields.pesel')}
              htmlFor={ids.pesel}
              hint={t('family.fields.peselHintSelf')}
            >
              <Input
                id={ids.pesel}
                inputMode="numeric"
                value={form.pesel}
                onChange={(event) => setForm({ ...form, pesel: event.target.value })}
              />
            </FormField>
            <FormField label={t('family.fields.documentKind')} htmlFor={ids.documentKind}>
              <Select
                id={ids.documentKind}
                value={form.documentKind}
                onChange={(event) =>
                  setForm({ ...form, documentKind: event.target.value as '' | '1' | '2' })
                }
              >
                <option value="">{t('family.fields.documentKindNone')}</option>
                <option value="1">{t('family.documentKinds.1')}</option>
                <option value="2">{t('family.documentKinds.2')}</option>
              </Select>
            </FormField>
            <FormField label={t('family.fields.documentNumber')} htmlFor={ids.documentNumber}>
              <Input
                id={ids.documentNumber}
                value={form.documentNumber}
                onChange={(event) => setForm({ ...form, documentNumber: event.target.value })}
              />
            </FormField>
            <FormField label={t('family.fields.birthDate')} htmlFor={ids.birthDate}>
              <Input
                id={ids.birthDate}
                type="date"
                value={form.birthDate}
                onChange={(event) => setForm({ ...form, birthDate: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('family.fields.disability')}
              htmlFor={ids.disability}
              hint={t('family.fields.disabilityHint')}
            >
              <Select
                id={ids.disability}
                value={form.disabilityCode}
                onChange={(event) => setForm({ ...form, disabilityCode: event.target.value })}
              >
                {DISABILITY_DEGREES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {t(`family.disability.${entry.code}`)}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label={t('family.fields.entitlementFrom')}
              htmlFor={ids.from}
              hint={t('family.fields.entitlementFromHintSelf')}
            >
              <Input
                id={ids.from}
                type="date"
                value={form.entitlementFrom}
                onChange={(event) => setForm({ ...form, entitlementFrom: event.target.value })}
              />
            </FormField>
            <Checkbox
              id={ids.household}
              label={t('family.fields.sharedHouseholdSelf')}
              checked={form.sharedHousehold}
              onChange={(event) => setForm({ ...form, sharedHousehold: event.target.checked })}
            />
          </>
        ) : (
          <>
            <FormField label={t('family.fields.member')} htmlFor={ids.member}>
              <Select
                id={ids.member}
                value={memberId}
                onChange={(event) => setMemberId(event.target.value)}
              >
                <option value="">{t('family.fields.memberPlaceholder')}</option>
                {active.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.firstName} {member.lastName}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label={t('family.fields.entitlementTo')}
              htmlFor={ids.to}
              hint={t('family.fields.entitlementToHintSelf')}
            >
              <Input
                id={ids.to}
                type="date"
                value={entitlementTo}
                onChange={(event) => setEntitlementTo(event.target.value)}
              />
            </FormField>
          </>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          {t('family.cancel')}
        </Button>
        <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
          {submit.isPending ? <Spinner label={t('family.saving')} /> : t('family.requestSubmit')}
        </Button>
      </div>
    </Modal>
  )
}
